import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Radio, Building2 } from "lucide-react";

const EMPTY = { kind: "anbieter", name: "", provider_type: "", external_id: "", supports_privat: true,
  supports_geschaeft: true, contact: {}, notes: "", active: true };

export default function MfProviders() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "mobilfunk:write");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["mf-providers", kind, q],
    queryFn: async () => (await api.get(`/mobilfunk/providers?kind=${kind}&q=${encodeURIComponent(q)}`)).data,
  });

  const toggle = async (p) => {
    try { await api.post(`/mobilfunk/providers/${p.id}/toggle`); qc.invalidateQueries({ queryKey: ["mf-providers"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  const columns = [
    { key: "name", header: "Name", render: (p) => (
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-medium">{p.name}{p.is_demo && <Badge variant="outline" className="text-[10px]">DEMO</Badge>}</div>
        <div className="text-xs text-muted-foreground">{p.provider_type || "—"} · {p.external_id || "ohne ID"}</div>
      </div>
    ) },
    { key: "kind", header: "Art", render: (p) => <Badge variant="secondary">{p.kind === "distributor" ? "Distributor" : "Anbieter"}</Badge> },
    { key: "segments", header: "Segmente", mobileHidden: true, render: (p) => (
      <div className="flex gap-1">{p.supports_privat && <Badge variant="outline" className="text-[10px]">Privat</Badge>}{p.supports_geschaeft && <Badge variant="outline" className="text-[10px]">Geschäft</Badge>}</div>
    ) },
    { key: "active", header: "Aktiv", render: (p) => (
      <Switch checked={!!p.active} disabled={!write} onCheckedChange={() => toggle(p)} data-testid={`provider-toggle-${p.id}`} onClick={(e) => e.stopPropagation()} />
    ) },
  ];

  return (
    <div data-testid="mf-providers">
      <PageHeader title="Anbieter & Distributoren" description={`${data?.total ?? 0} Einträge`}
        actions={write && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setEdit(EMPTY)} data-testid="new-provider-button"><Plus className="h-4 w-4" />Neu</Button>} />
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, ID, Typ…" className="pl-9" data-testid="provider-search" />
        </div>
        <Select value={kind || "all"} onValueChange={(v) => setKind(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="provider-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle</SelectItem><SelectItem value="anbieter">Anbieter</SelectItem><SelectItem value="distributor">Distributoren</SelectItem></SelectContent>
        </Select>
      </div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="provider-row" onRowClick={write ? (p) => setEdit(p) : undefined} />
      <ProviderDialog value={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["mf-providers"] }); }} />
    </div>
  );
}

function ProviderDialog({ value, onClose, onDone }) {
  const [f, setF] = useState(EMPTY);
  const isEdit = value && value.id;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  // sync when opened
  if (value && f.__id !== (value.id || "new")) setF({ ...EMPTY, ...value, __id: value.id || "new" });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name erforderlich");
    const body = { kind: f.kind, name: f.name, provider_type: f.provider_type, external_id: f.external_id,
      supports_privat: f.supports_privat, supports_geschaeft: f.supports_geschaeft, contact: f.contact || {}, notes: f.notes, active: f.active };
    try {
      if (isEdit) await api.put(`/mobilfunk/providers/${value.id}`, body);
      else await api.post("/mobilfunk/providers", body);
      toast.success("Gespeichert"); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="provider-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{f.kind === "distributor" ? <Building2 className="h-4 w-4" /> : <Radio className="h-4 w-4" />}{isEdit ? "Bearbeiten" : "Neu anlegen"}</DialogTitle>
          <DialogDescription>Anbieter und Distributoren werden als getrennte Einträge verwaltet.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Art</Label>
              <Select value={f.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger data-testid="provider-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="anbieter">Anbieter</SelectItem><SelectItem value="distributor">Distributor</SelectItem></SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Typ</Label><Input value={f.provider_type} onChange={(e) => set("provider_type", e.target.value)} placeholder="z. B. Netzbetreiber" /></div>
          </div>
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="provider-name-input" /></div>
          <div className="space-y-1.5"><Label>Externe ID</Label><Input value={f.external_id} onChange={(e) => set("external_id", e.target.value)} /></div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.supports_privat} onCheckedChange={(v) => set("supports_privat", v)} />Privatkunden</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.supports_geschaeft} onCheckedChange={(v) => set("supports_geschaeft", v)} />Geschäftskunden</label>
          </div>
          <div className="space-y-1.5"><Label>Notizen</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="provider-save">Speichern</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
